// Tiny client-side sort primitive used across every table in the app.
// Lists are bounded (rarely > a few hundred rows), so we don't push sort
// state to the server — react keeps the same cached rows and re-orders
// them on each header click.

import { useMemo, useState } from "react";

type SortValue = string | number | Date | null | undefined;
type Direction = "asc" | "desc";

export type Accessors<T> = Record<string, (row: T) => SortValue>;

/**
 * useSortableTable — pass the rows and a map of column-key → value-getter.
 * Returns the sorted view plus a header click-handler. Toggle direction
 * by clicking the same header twice; pick a different header and we
 * reset to ascending.
 */
export function useSortableTable<T>(
  rows: T[],
  accessors: Accessors<T>,
  initialKey: string | null = null,
  initialDir: Direction = "asc",
) {
  const [sortKey, setSortKey] = useState<string | null>(initialKey);
  const [sortDir, setSortDir] = useState<Direction>(initialDir);

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const accessor = accessors[sortKey];
    if (!accessor) return rows;
    const out = [...rows];
    out.sort((a, b) => compare(accessor(a), accessor(b)));
    if (sortDir === "desc") out.reverse();
    return out;
  }, [rows, sortKey, sortDir, accessors]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return { sorted, sortKey, sortDir, handleSort };
}

// nulls go to the end regardless of direction (the .reverse() flips
// non-null comparisons but null-vs-non-null stays the same).
function compare(a: SortValue, b: SortValue): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === "number" && typeof b === "number") return a - b;
  // ISO date strings compare lexicographically as chronological — this
  // path also handles plain strings.
  const as = String(a).toLowerCase();
  const bs = String(b).toLowerCase();
  return as < bs ? -1 : as > bs ? 1 : 0;
}

export function SortHeader({
  label, columnKey, sortKey, sortDir, onSort, align,
}: {
  label: React.ReactNode;
  columnKey: string;
  sortKey: string | null;
  sortDir: Direction;
  onSort: (key: string) => void;
  align?: "left" | "right" | "center";
}) {
  const active = sortKey === columnKey;
  return (
    <th
      onClick={() => onSort(columnKey)}
      style={{
        cursor: "pointer",
        userSelect: "none",
        textAlign: align ?? "left",
        whiteSpace: "nowrap",
      }}
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
    >
      {label}
      <span style={{ marginLeft: "0.3rem", opacity: active ? 0.8 : 0.25 }} aria-hidden="true">
        {active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
      </span>
    </th>
  );
}
